import React, {useState, useEffect, useRef} from 'react';
import styles from '../tables.module.css';
import localStyles from './UniversityResearchRequestsTable.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";
import {downloadCsv, toUtcIso} from './shared/downloadCsv';

const TRACK_LABELS = {
    databank: 'Data bank access',
    custom: 'Custom collection',
};

const ROLES = ['Faculty', 'Postdoc', 'PhD student', "Master's student", 'Undergraduate', 'Other'];

const ENDPOINT = `${PRISMAX_BACKEND_URL}/api/admin/get-marketing-university-research-requests`;

const formatOutcomes = (value) => (Array.isArray(value) ? value.join(', ') : value || '');

const formatNeed = (item) => {
    if (item.track === 'custom') return item.data_needs || '';
    return item.data_amount ? `${item.data_amount} ${item.data_amount_unit || ''}`.trim() : '';
};

const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const buildQuery = (page, pageSize, track, role) => {
    const params = new URLSearchParams({page: String(page), page_size: String(pageSize)});
    if (track) params.set('track', track);
    if (role) params.set('role', role);
    return params.toString();
};

const fetchPage = async (query) => {
    const token = localStorage.getItem("prismax_admin_token");
    const response = await fetch(`${ENDPOINT}?${query}`, {
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        throw new Error('Failed to fetch university research requests');
    }
    const data = await response.json();
    if (!data.success || !data.data) {
        throw new Error('Invalid data format');
    }
    return data;
};

const UniversityResearchRequestsTable = () => {
    const [requestsData, setRequestsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [csvError, setCsvError] = useState('');
    const [downloadingCsv, setDownloadingCsv] = useState(false);
    const [trackFilter, setTrackFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const isInitialLoad = useRef(true);

    useEffect(() => {
        const fetchRequestsData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await fetchPage(buildQuery(currentPage, itemsPerPage, trackFilter, roleFilter));
                setRequestsData(data.data);
                setTotalPages(data.total_pages || 1);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        };

        fetchRequestsData();
    }, [currentPage, trackFilter, roleFilter]);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const onTrackChange = (e) => {
        setTrackFilter(e.target.value);
        setCurrentPage(1);
    };

    const onRoleChange = (e) => {
        setRoleFilter(e.target.value);
        setCurrentPage(1);
    };

    const downloadAllCsv = async () => {
        setDownloadingCsv(true);
        setCsvError('');
        try {
            const allRows = [];
            let page = 1;
            let pages = 1;
            do {
                const data = await fetchPage(buildQuery(page, 500, trackFilter, roleFilter));
                allRows.push(...data.data);
                pages = data.total_pages || 1;
                page += 1;
            } while (page <= pages);
            downloadCsv('university-research-requests', [
                'Request ID', 'Created At', 'Full Name', 'Email', 'Role', 'Institution', 'Research Lab', 'Lab Website',
                'Principal Investigator', 'PI Email', 'Research Topic', 'Expected Outcomes', 'Target Publish Date',
                'Track', 'Data Amount', 'Data Amount Unit', 'Data Needs', 'Notification Email Sent', 'IP Address', 'Location',
            ], allRows.map((item) => [
                item.university_research_request_id,
                toUtcIso(item.created_at),
                item.full_name,
                item.email,
                item.role,
                item.institution,
                item.research_lab,
                item.lab_website,
                item.principal_investigator,
                item.pi_email,
                item.research_topic,
                formatOutcomes(item.expected_outcomes),
                item.target_publish_date,
                TRACK_LABELS[item.track] || item.track,
                item.data_amount,
                item.data_amount_unit,
                item.data_needs,
                item.notification_email_sent ? 'Yes' : 'No',
                item.ip_address,
                item.location,
            ]));
        } catch (err) {
            setCsvError(err.message);
        } finally {
            setDownloadingCsv(false);
        }
    };

    if (loading && isInitialLoad.current) {
        return <div className={styles.loading}>Loading university research requests...</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeaderRow}>
                <h2 className={styles.tableTitle}>University Research</h2>
                <div className={styles.headerActions}>
                    <div className={localStyles.filters}>
                        <label className={localStyles.filterLabel}>
                            Track
                            <select className={styles.select} value={trackFilter} onChange={onTrackChange}>
                                <option value="">All tracks</option>
                                {Object.entries(TRACK_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>
                        <label className={localStyles.filterLabel}>
                            Role
                            <select className={styles.select} value={roleFilter} onChange={onRoleChange}>
                                <option value="">All roles</option>
                                {ROLES.map((role) => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <button type="button" className={styles.csvButton} disabled={downloadingCsv} onClick={downloadAllCsv}>
                        {downloadingCsv ? 'Preparing…' : 'Download CSV'}
                    </button>
                </div>
            </div>
            {csvError ? <div role="alert" className={styles.actionError}>{csvError}</div> : null}
            {error ? <div className={styles.error}>Error: {error}</div> : null}
            {!error && (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>Request ID</th>
                            <th>Created At</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Institution</th>
                            <th>Lab</th>
                            <th>PI</th>
                            <th>PI Email</th>
                            <th>Track</th>
                            <th>Expected Outcomes</th>
                            <th>Target Publish Date</th>
                            <th>Amount / Data Needs</th>
                            <th>Research Topic</th>
                            <th>IP Address</th>
                            <th>Location</th>
                        </tr>
                        </thead>
                        <tbody>
                        {requestsData.length > 0 ? (
                            requestsData.map((item) => (
                                <tr key={item.university_research_request_id}>
                                    <td>{item.university_research_request_id}</td>
                                    <td className={localStyles.nowrap}>{new Date(item.created_at).toLocaleString()}</td>
                                    <td>{item.full_name}</td>
                                    <td>{item.email}</td>
                                    <td>{item.role}</td>
                                    <td>{item.institution}</td>
                                    <td>
                                        {isHttpUrl(item.lab_website) ? (
                                            <a className={localStyles.link} href={item.lab_website} target="_blank" rel="noopener noreferrer">
                                                {item.research_lab}
                                            </a>
                                        ) : item.research_lab}
                                    </td>
                                    <td>{item.principal_investigator}</td>
                                    <td>{item.pi_email || '—'}</td>
                                    <td className={localStyles.nowrap}>{TRACK_LABELS[item.track] || item.track}</td>
                                    <td>{formatOutcomes(item.expected_outcomes)}</td>
                                    <td>{item.target_publish_date || '—'}</td>
                                    <td>
                                        <div className={localStyles.clamp} title={formatNeed(item)}>{formatNeed(item) || '—'}</div>
                                    </td>
                                    <td>
                                        <div className={localStyles.clamp} title={item.research_topic}>{item.research_topic}</div>
                                    </td>
                                    <td>{item.ip_address}</td>
                                    <td>{item.location}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="16" className={styles.noData}>
                                    {loading ? 'Loading…' : 'No university research requests found'}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}

            {requestsData.length > 0 && !error && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default UniversityResearchRequestsTable;
